import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { a as anime } from './anime.es.js';
import { a as createLogger, g as getCSSValue, f as isUnset, u as updateElementStyle, A as updateColorElementStyle, d as isNumber, n as numToRem } from './utils.js';

// Constants
const component = 'cl-icon';

const clIconCss = ".sc-cl-icon-h{align-items:center;display:inline-flex;justify-content:center}.sc-cl-icon-h svg.sc-cl-icon,.sc-cl-icon-h use.sc-cl-icon,.sc-cl-icon-h slot-fb.sc-cl-icon,.sc-cl-icon-h.sc-cl-icon-s>*{display:block;fill:var(--custom-icon-color, var(--icon-color, currentColor));height:var(--custom-icon-size, var(--icon-size, var(--size-3)));transform-origin:center;width:var(--custom-icon-size, var(--icon-size, var(--size-3)))}.sc-cl-icon-h svg.sc-cl-icon{overflow:visible}.sc-cl-icon-h.sc-cl-icon-s>*:not(:last-child){display:none}.sc-cl-icon-h.sc-cl-icon-s>*:last-child{display:block}";

const ClIcon = /*@__PURE__*/ proxyCustomElement(class ClIcon extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * Value of the --animation-duration-normal CSS token
     */
    this.animationDuration = +(getCSSValue('--animation-duration-normal') || '250ms').replace('ms', '');
    /**
     * Handles animating the icon out
     */
    this._animateIconOut = (name) => {
      anime({
        targets: this.useElement,
        opacity: 0,
        scale: 0.5,
        duration: this.animationDuration / 2.5,
      }).finished.then(() => {
        if (this.useElement) {
          this.useElement.setAttribute('href', `#${name}`);
        }
        this._animateIconIn();
      });
    };
    /**
     * Handles animating the icon in
     */
    this._animateIconIn = () => {
      anime({
        targets: this.useElement,
        opacity: 1,
        scale: 1,
        duration: this.animationDuration,
      });
    };
    this.ariaLabel = '';
    this.fillColor = undefined;
    this.name = '';
    this.size = undefined;
  }
  /**
   * Validates the fillColor property and updates the styles accordingly
   */
  handleFillColor(fillColor) {
    const cssVariable = '--custom-icon-color';
    if (isUnset(fillColor)) {
      updateElementStyle(this.element, undefined, cssVariable);
      return;
    }
    updateColorElementStyle(this.element, fillColor, cssVariable, this.logger.error.attribute.fillColor);
  }
  /**
   * Animate when name value changes
   */
  handleName(name, previousName) {
    if (name !== previousName) {
      this._animateIconOut(name);
    }
  }
  /**
   * Validates the size property and updates the styles accordingly
   */
  handleSize(size) {
    const cssVariable = '--custom-icon-size';
    switch (true) {
      case isUnset(size):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !isNumber(size):
        this.logger.error.attribute.size(`Given invalid value "${size}".`);
        break;
      default:
        updateElementStyle(this.element, numToRem(size), cssVariable);
        break;
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Validate the size attribute and update the styles;
   * - Validate the fillColor attribute and update the styles;
   */
  connectedCallback() {
    this.handleSize(this.size);
    this.handleFillColor(this.fillColor);
  }
  /**
   * Once after the component has fully loaded and rendered:
   * - Set href on the use element ;
   */
  componentDidLoad() {
    if (this.useElement) {
      this.useElement.setAttribute('href', `#${this.name}`);
    }
  }
  render() {
    var _a;
    return (h(Host, Object.assign({}, (this.ariaLabel ? { 'aria-label': this.ariaLabel } : {})), h("slot", null, ((_a = this.name) === null || _a === void 0 ? void 0 : _a.length) ? (h("svg", null, h("use", { ref: el => {
        this.useElement = el;
      } }))) : null)));
  }
  get element() { return this; }
  static get watchers() { return {
    "fillColor": ["handleFillColor"],
    "name": ["handleName"],
    "size": ["handleSize"]
  }; }
  static get style() { return clIconCss; }
}, [6, "cl-icon", {
    "ariaLabel": [1, "aria-label"],
    "fillColor": [1, "fill-color"],
    "name": [1],
    "size": [2]
  }]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-icon"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-icon":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClIcon);
      }
      break;
  } });
}

export { ClIcon as C, defineCustomElement as d };

//# sourceMappingURL=cl-icon2.js.map